import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useState,
} from "react";

import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import { ZodSchema, z } from "zod";

import { SearchQueryParams, getUrlSearchParams } from "./searchParams.helpers";

type SearchParamsContextType<Schema extends ZodSchema> = {
  searchParams: Partial<z.infer<Schema>> | null;
  setSearchParamsHandler: (
    queryParams: SearchQueryParams | string,
    schema: Schema,
  ) => void;
};

const SearchParamsContext = createContext<SearchParamsContextType<TODO> | null>(
  null,
);

const useCreateSearchParamsContextValue = <Schema extends ZodSchema>(
  defaultValue: string | SearchQueryParams,
  schema: Schema,
) => {
  const pathName = usePathname();
  const router = useRouter();
  const [searchParams, setSearchParams] = useState(
    getUrlSearchParams(defaultValue, schema),
  );

  const searchParamsFromUrl = useSearchParams();

  useEffect(() => {
    // set params from url when url changes
    const queryParams = Object.fromEntries(searchParamsFromUrl.entries());
    const parsedQueryParams = schema.safeParse(queryParams);

    if (parsedQueryParams.success) {
      setSearchParams(parsedQueryParams.data);
    }
  }, [defaultValue, schema, searchParamsFromUrl]);

  const setSearchParamsHandler = useCallback(
    (queryParams: z.infer<Schema>) => {
      setSearchParams(getUrlSearchParams(queryParams, schema));
      router.push(
        `${pathName}?${new URLSearchParams(queryParams).toString()}`,
        undefined,
        { shallow: true },
      );
    },
    [pathName, router, schema],
  );

  return {
    /**
     * @deprecated use useSearchParamsContextV2 instead
     */
    searchParams,
    /**
     * @deprecated use useSearchParamsContextV2 instead; this function was written with type errors
     */
    setSearchParamsHandler: setSearchParamsHandler as (
      queryParams: SearchQueryParams | string,
      schema: ZodSchema,
    ) => void,
  };
};

type PropTypes<Schema extends ZodSchema> = PropsWithChildren & {
  defaultValue: SearchQueryParams | string;
  schema: Schema;
};

/**
 * @deprecated use SearchParamsContextProviderV2 instead
 */
export const SearchParamsContextProvider = <Schema extends ZodSchema>({
  children,
  defaultValue,
  schema,
}: PropTypes<Schema>): React.ReactElement => {
  const value = useCreateSearchParamsContextValue<Schema>(
    defaultValue,
    schema,
  ) as SearchParamsContextType<Schema>;

  return (
    <SearchParamsContext.Provider value={value}>
      {children}
    </SearchParamsContext.Provider>
  );
};

/**
 * @deprecated use useSearchParamsContextV2 instead
 */
export const useSearchParamsContext = <Schema extends ZodSchema>(
  _schema: Schema,
) => {
  const context = React.useContext<SearchParamsContextType<Schema>>(
    SearchParamsContext as React.Context<SearchParamsContextType<Schema>>,
  );

  if (!context) {
    throw new Error(
      "useSearchParamsContext must be used within a SearchParamsContextProvider",
    );
  }

  return context;
};
